import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmationDialog,
  DatePicker,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@roger/ui-components';
import {
  ChevronDown,
  ChevronRight,
  Download,
  IdCard,
  Loader2,
  Paperclip,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  createUserLicenseUserLicensesPost,
  deleteUserLicenseDocumentUserLicensesLicenseIdDocumentsDocumentIdDelete,
  deleteUserLicenseUserLicensesLicenseIdDelete,
  getUserLicenseDocumentUrlUserLicensesLicenseIdDocumentsDocumentIdUrlGet,
  listUserLicensesUserLicensesGet,
  listUserLicenseTypesForPickerUserLicenseTypesGet,
  updateUserLicenseUserLicensesLicenseIdPut,
  uploadUserLicenseDocumentUserLicensesLicenseIdDocumentsPost,
} from '../../services/api/sdk.gen';
import type {
  UserLicenseResponse,
  UserLicenseTypeResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateToISO, formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import { getLicenseExpiryStatus, LICENSE_EXPIRY_LABELS } from './licenseStatus';
import { resolveDeleteTarget, type PendingDelete } from './licenseDeleteTarget';

type UserLicensesSectionProps = {
  userId: string;
  /** Whether the operator may add, change or remove licenses and documents. */
  canEdit: boolean;
  /** False when the section has a tab of its own: always open, no toggle. */
  collapsible?: boolean;
  /**
   * Inside a modal of its own (the members grid): every license is a row of
   * controls edited in place, with a blank row underneath for the next one.
   */
  inDialog?: boolean;
};

export const FieldLabel: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = '', children }) => (
  <span className={`text-xs font-medium text-muted-foreground ${className}`}>
    {children}
  </span>
);

// One template for every box, so the columns line up down the list. A phone
// stacks the fields two to a line instead.
const LICENSE_GRID =
  'grid grid-cols-2 items-start gap-x-3 gap-y-3 md:grid-cols-[minmax(0,1fr)_8rem_8rem_7rem_2rem] md:gap-y-1';

/** A label above its control, filling one cell of the license grid. */
const LicenseField: React.FC<{
  label: string;
  className?: string;
  children: React.ReactNode;
}> = ({ label, className = '', children }) => (
  <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </div>
);

type LicenseFormState = {
  licenseTypeId: string;
  initiationDate: Date | undefined;
  expirationDate: Date | undefined;
  notes: string;
};

const EMPTY_FORM: LicenseFormState = {
  licenseTypeId: '',
  initiationDate: undefined,
  expirationDate: undefined,
  notes: '',
};

const licenseBody = (fields: LicenseFormState) => ({
  license_type_id: fields.licenseTypeId,
  // formatDateToISO, not formatDateToYYYYMMDD: the latter strips the
  // dashes for OASIS answers and the API wants a calendar date.
  initiation_date: fields.initiationDate
    ? formatDateToISO(fields.initiationDate)
    : null,
  expiration_date: fields.expirationDate
    ? formatDateToISO(fields.expirationDate)
    : null,
  notes: fields.notes.trim() || null,
});

const fieldsOf = (license: UserLicenseResponse): LicenseFormState => ({
  licenseTypeId: license.license_type_id,
  initiationDate: parseISOToLocalDate(license.initiation_date),
  expirationDate: parseISOToLocalDate(license.expiration_date),
  notes: license.notes ?? '',
});

const datesOutOfOrder = (fields: LicenseFormState) =>
  !!fields.initiationDate &&
  !!fields.expirationDate &&
  fields.expirationDate < fields.initiationDate;

const BODY_KEYS = {
  licenseTypeId: 'license_type_id',
  initiationDate: 'initiation_date',
  expirationDate: 'expiration_date',
  notes: 'notes',
} as const;

const DATES_OUT_OF_ORDER =
  'The expiration date cannot be before the initiation date.';

const EXPIRY_BADGE_STYLES: Record<'expired' | 'expiring', string> = {
  expired: 'border-red-300 bg-red-50 text-red-800',
  expiring: 'border-amber-300 bg-amber-50 text-amber-800',
};

const ExpiryBadge: React.FC<{
  expirationDate: string | null | undefined;
  className?: string;
}> = ({ expirationDate, className = '' }) => {
  const status = getLicenseExpiryStatus(expirationDate);
  if (!status) return null;
  return (
    <Badge
      variant='outline'
      className={`w-fit whitespace-nowrap text-xs ${EXPIRY_BADGE_STYLES[status]} ${className}`}
    >
      {LICENSE_EXPIRY_LABELS[status]}
    </Badge>
  );
};

/**
 * One license as a row of controls. Used for the saved rows and the blank
 * row alike, so a new license looks like the ones above it from the start.
 */
const LicenseEditRow: React.FC<{
  fields: LicenseFormState;
  types: UserLicenseTypeResponse[];
  disabled?: boolean;
  onChange: (patch: Partial<LicenseFormState>) => void;
  expiryBadge?: React.ReactNode;
  documents: React.ReactNode;
  action: React.ReactNode;
  /** The row for the next license, drawn lighter than the saved ones. */
  blank?: boolean;
  /** A saved license's type name, offered even once the type is retired. */
  typeName?: string;
}> = ({
  fields,
  types,
  disabled,
  onChange,
  expiryBadge,
  documents,
  action,
  blank,
  typeName,
}) => {
  // Notes save when the field is left, not on every keystroke.
  const [notes, setNotes] = useState(fields.notes);
  useEffect(() => setNotes(fields.notes), [fields.notes]);
  // Closing the dialog can unmount the field without a blur; save on the way out.
  const pendingNotesRef = useRef({ notes, saved: fields.notes, onChange });
  pendingNotesRef.current = { notes, saved: fields.notes, onChange };
  useEffect(
    () => () => {
      const pending = pendingNotesRef.current;
      if (pending.notes !== pending.saved) {
        pending.onChange({ notes: pending.notes });
      }
    },
    []
  );
  const retiredType =
    typeName &&
    fields.licenseTypeId &&
    !types.some(type => type.id === fields.licenseTypeId);
  return (
    <div
      className={`relative ${LICENSE_GRID} rounded-md border px-3 py-3 ${blank ? 'border-dashed' : ''}`}
    >
      <div className='absolute right-2 top-2'>{action}</div>
      <LicenseField label='Type' className='col-span-2 md:col-span-1'>
        <Select
          value={fields.licenseTypeId}
          disabled={disabled}
          onValueChange={value => onChange({ licenseTypeId: value })}
        >
          <SelectTrigger selectSize='sm' aria-label='License type'>
            <SelectValue placeholder='Add a license' />
          </SelectTrigger>
          <SelectContent>
            {retiredType && (
              <SelectItem value={fields.licenseTypeId}>
                {typeName} (retired)
              </SelectItem>
            )}
            {types.map(type => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </LicenseField>
      <LicenseField label='Initiated'>
        <DatePicker
          datePickerSize='sm'
          // Its own default width is 280px.
          className='w-full'
          date={fields.initiationDate}
          maxDate={fields.expirationDate}
          disabled={disabled}
          onDateChange={date => onChange({ initiationDate: date })}
          placeholder='—'
          showClearButton
          aria-label='Initiation date'
        />
      </LicenseField>
      <LicenseField label='Expires'>
        <DatePicker
          datePickerSize='sm'
          className='w-full'
          date={fields.expirationDate}
          minDate={fields.initiationDate}
          disabled={disabled}
          onDateChange={date => onChange({ expirationDate: date })}
          placeholder='—'
          showClearButton
          aria-label='Expiration date'
        />
      </LicenseField>
      <div className='col-span-2 empty:hidden md:col-span-1 md:flex md:h-7 md:items-center md:self-end md:empty:flex'>
        {expiryBadge}
      </div>
      <span className='hidden md:block' />
      {/* Second line: documents under the type, notes across the rest. */}
      <LicenseField
        label='Documents'
        className='col-span-2 md:col-span-1 md:mt-2'
      >
        <div className='pt-1'>{documents}</div>
      </LicenseField>
      <LicenseField label='Notes' className='col-span-2 md:col-span-4 md:mt-2'>
        <Input
          inputSize='sm'
          value={notes}
          disabled={disabled}
          onChange={event => setNotes(event.target.value)}
          onBlur={() => {
            if (notes !== fields.notes) onChange({ notes });
          }}
          placeholder='Notes: license number, issuing state'
          aria-label='Notes'
        />
      </LicenseField>
    </div>
  );
};

/**
 * The licenses an agency holds on file for one member of staff.
 *
 * Writes go straight to the API rather than joining the dialog's Save Changes
 * pipeline, following `RogerDocAccountSection`. A file upload cannot be deferred
 * to a Save button without inventing somewhere to stage the bytes, and a
 * half-applied save that committed three licenses and failed on the fourth
 * would be worse than four independent actions that each report their own
 * outcome.
 *
 * Nothing loads until the section is expanded: it sits below several other
 * sections in an already tall dialog, and most visits to that dialog are not
 * about licenses.
 */
export const UserLicensesSection: React.FC<UserLicensesSectionProps> =
  React.memo(function UserLicensesSection({
    userId,
    canEdit,
    collapsible = true,
    inDialog = false,
  }) {
    const { showSuccess, showError } = useStore(
      useShallow(state => ({
        showSuccess: state.showSuccess,
        showError: state.showError,
      }))
    );

    const [expanded, setExpanded] = useState(!collapsible);
    const [isLoading, setIsLoading] = useState(false);
    const [licenses, setLicenses] = useState<UserLicenseResponse[]>([]);
    const [types, setTypes] = useState<UserLicenseTypeResponse[]>([]);
    const [busyLicenseId, setBusyLicenseId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<LicenseFormState>(EMPTY_FORM);
    // Both destructive actions arm the same confirmation, so only one can ever
    // be pending and neither can be left armed against a row that has gone. A
    // nested ConfirmationDialog is the pattern the rest of the product uses for
    // this, including from inside another dialog.
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
      null
    );

    // Monotonic load generation: only the newest load may write to the table, so
    // switching members quickly cannot leave one person's licenses rendered
    // under another's name.
    const requestIdRef = useRef(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadTargetRef = useRef<string | null>(null);

    // `quiet` reloads behind the rows after an in-place edit: the spinner
    // would push the rows down and the whole editor would appear to flash.
    const loadLicenses = useCallback(
      async (quiet = false) => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        const isCurrent = () => requestIdRef.current === requestId;
        if (!quiet) setIsLoading(true);
        try {
          const [licenseResult, typeResult] = await Promise.all([
            listUserLicensesUserLicensesGet({
              query: { user_id: userId },
              throwOnError: true,
            }),
            listUserLicenseTypesForPickerUserLicenseTypesGet({
              throwOnError: true,
            }),
          ]);
          if (isCurrent()) {
            setLicenses(licenseResult.data ?? []);
            setTypes(typeResult.data ?? []);
          }
        } catch (error) {
          console.error('Failed to load licenses:', error);
          if (isCurrent()) {
            showError('Unable to load licenses. Please try again.');
          }
        } finally {
          if (isCurrent() && !quiet) setIsLoading(false);
        }
      },
      [userId, showError]
    );

    useEffect(() => {
      // Collapse and drop the rows when the dialog moves to another member, so
      // an expanded section never shows the previous person's file.
      //
      // The generation bump is what makes that hold. Clearing the rows does not
      // cancel a load already in flight for the member we just left, and that
      // response still reads as current -- so it would repopulate `licenses`
      // and the next expand would show the previous person's credentials until
      // the new load overwrote them.
      requestIdRef.current += 1;
      // A section shown without its toggle has no way to reopen, so it stays
      // open (and so reloads for the new member).
      setExpanded(!collapsible);
      setLicenses([]);
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setPendingDelete(null);
    }, [userId, collapsible]);

    useEffect(() => {
      if (expanded) void loadLicenses();
    }, [expanded, loadLicenses]);

    // In place (inDialog): a Select or date saves when picked, notes when the
    // field is left. The blank row becomes a license once it has a type,
    // taking any dates and notes typed before that.
    const [draft, setDraft] = useState<LicenseFormState>(EMPTY_FORM);
    useEffect(() => setDraft(EMPTY_FORM), [userId]);

    const reportSaveError = useCallback(
      (error: unknown) => {
        console.error('Failed to save license:', error);
        showError(
          getBusinessErrorMessage(error) ??
            'Failed to save this license. Please try again.'
        );
      },
      [showError]
    );

    const saveInPlace = useCallback(
      async (
        license: UserLicenseResponse,
        patch: Partial<LicenseFormState>
      ) => {
        const fields = { ...fieldsOf(license), ...patch };
        if (datesOutOfOrder(fields)) {
          showError(DATES_OUT_OF_ORDER);
          return;
        }
        // Only the edited keys, so overlapping saves cannot undo each other.
        const full = licenseBody(fields);
        const body = Object.fromEntries(
          (Object.keys(patch) as (keyof LicenseFormState)[]).map(key => [
            BODY_KEYS[key],
            full[BODY_KEYS[key]],
          ])
        ) as Partial<typeof full>;
        // Shown at once and kept; a failed save reloads from the server.
        setLicenses(prev =>
          prev.map(l =>
            l.id === license.id
              ? {
                  ...l,
                  ...body,
                  license_type_name:
                    types.find(t => t.id === fields.licenseTypeId)?.name ??
                    l.license_type_name,
                }
              : l
          )
        );
        try {
          await updateUserLicenseUserLicensesLicenseIdPut({
            path: { license_id: license.id },
            body,
            throwOnError: true,
          });
        } catch (error) {
          reportSaveError(error);
          await loadLicenses(true);
        }
      },
      [types, showError, reportSaveError, loadLicenses]
    );

    const createFromDraft = useCallback(
      async (fields: LicenseFormState) => {
        if (datesOutOfOrder(fields)) {
          showError(DATES_OUT_OF_ORDER);
          setDraft({ ...fields, licenseTypeId: '' });
          return;
        }
        setIsSubmitting(true);
        try {
          await createUserLicenseUserLicensesPost({
            body: { ...licenseBody(fields), user_id: userId },
            throwOnError: true,
          });
          await loadLicenses(true);
          setDraft(EMPTY_FORM);
        } catch (error) {
          reportSaveError(error);
          // Unpicked, so picking the type again retries.
          setDraft({ ...fields, licenseTypeId: '' });
        } finally {
          setIsSubmitting(false);
        }
      },
      [userId, loadLicenses, showError, reportSaveError]
    );

    const openCreateForm = useCallback(() => {
      setEditingId(null);
      setForm(EMPTY_FORM);
      setShowForm(true);
    }, []);

    const openEditForm = useCallback((license: UserLicenseResponse) => {
      setEditingId(license.id);
      setForm(fieldsOf(license));
      setShowForm(true);
    }, []);

    const closeForm = useCallback(() => {
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    }, []);

    const submitForm = useCallback(async () => {
      if (!form.licenseTypeId) {
        showError('Choose a license type.');
        return;
      }
      if (datesOutOfOrder(form)) {
        showError(DATES_OUT_OF_ORDER);
        return;
      }
      setIsSubmitting(true);
      try {
        const body = licenseBody(form);
        if (editingId) {
          await updateUserLicenseUserLicensesLicenseIdPut({
            path: { license_id: editingId },
            body,
            throwOnError: true,
          });
          showSuccess('License updated');
        } else {
          await createUserLicenseUserLicensesPost({
            body: { ...body, user_id: userId },
            throwOnError: true,
          });
          showSuccess('License added');
        }
        closeForm();
        await loadLicenses();
      } catch (error) {
        reportSaveError(error);
      } finally {
        setIsSubmitting(false);
      }
    }, [
      form,
      editingId,
      userId,
      closeForm,
      loadLicenses,
      showSuccess,
      showError,
      reportSaveError,
    ]);

    const removeLicense = useCallback(
      async (license: UserLicenseResponse) => {
        setBusyLicenseId(license.id);
        setPendingDelete(null);
        try {
          await deleteUserLicenseUserLicensesLicenseIdDelete({
            path: { license_id: license.id },
            throwOnError: true,
          });
          showSuccess('License removed');
          await loadLicenses(inDialog);
        } catch (error) {
          console.error('Failed to remove license:', error);
          showError(
            getBusinessErrorMessage(error) ??
              'Failed to remove this license. Please try again.'
          );
        } finally {
          setBusyLicenseId(null);
        }
      },
      [loadLicenses, showSuccess, showError, inDialog]
    );

    const beginUpload = useCallback((licenseId: string) => {
      uploadTargetRef.current = licenseId;
      fileInputRef.current?.click();
    }, []);

    const handleFileSelected = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const licenseId = uploadTargetRef.current;
        // Reset immediately so choosing the same file twice still fires change.
        event.target.value = '';
        uploadTargetRef.current = null;
        if (!file || !licenseId) return;

        setBusyLicenseId(licenseId);
        try {
          await uploadUserLicenseDocumentUserLicensesLicenseIdDocumentsPost({
            path: { license_id: licenseId },
            body: { file },
            throwOnError: true,
          });
          showSuccess(`${file.name} attached`);
          await loadLicenses(inDialog);
        } catch (error) {
          console.error('Failed to upload document:', error);
          // The refusals worth reading are the validation ones (wrong type, too
          // large), which the backend words for the operator.
          showError(
            getBusinessErrorMessage(error) ??
              'Failed to attach this file. Please try again.'
          );
        } finally {
          setBusyLicenseId(null);
        }
      },
      [loadLicenses, showSuccess, showError, inDialog]
    );

    const openDocument = useCallback(
      async (licenseId: string, documentId: string) => {
        try {
          const result =
            await getUserLicenseDocumentUrlUserLicensesLicenseIdDocumentsDocumentIdUrlGet(
              {
                path: { license_id: licenseId, document_id: documentId },
                throwOnError: true,
              }
            );
          const url = result.data?.url;
          if (!url) throw new Error('No download URL returned');
          window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error) {
          console.error('Failed to open document:', error);
          showError('Unable to open this file. Please try again.');
        }
      },
      [showError]
    );

    const removeDocument = useCallback(
      async (licenseId: string, documentId: string) => {
        setBusyLicenseId(licenseId);
        setPendingDelete(null);
        try {
          await deleteUserLicenseDocumentUserLicensesLicenseIdDocumentsDocumentIdDelete(
            {
              path: { license_id: licenseId, document_id: documentId },
              throwOnError: true,
            }
          );
          showSuccess('Document removed');
          await loadLicenses(inDialog);
        } catch (error) {
          console.error('Failed to remove document:', error);
          showError('Failed to remove this file. Please try again.');
        } finally {
          setBusyLicenseId(null);
        }
      },
      [loadLicenses, showSuccess, showError, inDialog]
    );

    return (
      <div>
        {collapsible && <Separator className='mb-4' />}
        <div className='space-y-3'>
          {collapsible ? (
            <button
              type='button'
              className='flex w-full items-center gap-2 text-left'
              onClick={() => setExpanded(open => !open)}
              aria-expanded={expanded}
            >
              {expanded ? (
                <ChevronDown className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
              ) : (
                <ChevronRight className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
              )}
              <IdCard className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
              <h3 className='text-sm font-semibold'>Licenses</h3>
              {expanded && isLoading && (
                <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
              )}
            </button>
          ) : (
            isLoading && (
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            )
          )}

          {expanded && (
            <div className='space-y-3'>
              {canEdit && !showForm && !inDialog && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={openCreateForm}
                >
                  <Plus className='mr-1 h-3.5 w-3.5' />
                  Add license
                </Button>
              )}

              {inDialog && (
                <div className='space-y-2'>
                  {licenses.map(license => (
                    <LicenseEditRow
                      key={license.id}
                      fields={fieldsOf(license)}
                      types={types}
                      typeName={license.license_type_name}
                      disabled={!canEdit || busyLicenseId === license.id}
                      onChange={patch => void saveInPlace(license, patch)}
                      expiryBadge={
                        <ExpiryBadge expirationDate={license.expiration_date} />
                      }
                      documents={
                        <div className='flex flex-col gap-1'>
                          {(license.documents ?? []).map(document => (
                            <div
                              key={document.id}
                              className='flex items-center gap-1'
                            >
                              <button
                                type='button'
                                className='flex min-w-0 items-center gap-1 text-xs text-primary hover:underline'
                                onClick={() =>
                                  openDocument(license.id, document.id)
                                }
                              >
                                <Download className='h-3 w-3 shrink-0' />
                                <span className='truncate'>
                                  {document.file_name}
                                </span>
                              </button>
                              {canEdit && (
                                <button
                                  type='button'
                                  className='shrink-0 rounded p-0.5 hover:bg-muted'
                                  aria-label={`Remove ${document.file_name}`}
                                  disabled={busyLicenseId === license.id}
                                  onClick={() =>
                                    setPendingDelete({
                                      kind: 'document',
                                      licenseId: license.id,
                                      documentId: document.id,
                                      label: document.file_name,
                                    })
                                  }
                                >
                                  <Trash2 className='h-3 w-3 text-muted-foreground' />
                                </button>
                              )}
                            </div>
                          ))}
                          {canEdit && (
                            <button
                              type='button'
                              className='flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
                              disabled={busyLicenseId === license.id}
                              onClick={() => beginUpload(license.id)}
                            >
                              {busyLicenseId === license.id ? (
                                <Loader2 className='h-3 w-3 animate-spin' />
                              ) : (
                                <Paperclip className='h-3 w-3' />
                              )}
                              Attach
                            </button>
                          )}
                        </div>
                      }
                      action={
                        canEdit && (
                          <button
                            type='button'
                            className='rounded p-1 hover:bg-muted'
                            aria-label={`Remove ${license.license_type_name}`}
                            disabled={busyLicenseId === license.id}
                            onClick={() =>
                              setPendingDelete({
                                kind: 'license',
                                licenseId: license.id,
                                label: license.license_type_name,
                              })
                            }
                          >
                            <Trash2 className='h-3.5 w-3.5 text-destructive' />
                          </button>
                        )
                      }
                    />
                  ))}
                  {canEdit && (
                    <LicenseEditRow
                      blank
                      fields={draft}
                      types={types}
                      disabled={isSubmitting}
                      onChange={patch => {
                        const next = { ...draft, ...patch };
                        setDraft(next);
                        if (next.licenseTypeId) void createFromDraft(next);
                      }}
                      documents={
                        <span
                          className='text-xs text-muted-foreground'
                          title='Choose a type first'
                        >
                          —
                        </span>
                      }
                      action={
                        isSubmitting && (
                          <Loader2 className='h-3.5 w-3.5 animate-spin text-muted-foreground' />
                        )
                      }
                    />
                  )}
                </div>
              )}

              {!inDialog && showForm && (
                <div className='space-y-3 rounded-md border p-3'>
                  <div className='flex flex-col gap-3 md:flex-row'>
                    <div className='min-w-0 flex-1 space-y-1'>
                      <Label className='text-xs'>Type</Label>
                      <Select
                        value={form.licenseTypeId}
                        onValueChange={value =>
                          setForm(prev => ({ ...prev, licenseTypeId: value }))
                        }
                      >
                        <SelectTrigger
                          selectSize='sm'
                          aria-label='License type'
                        >
                          <SelectValue placeholder='Choose a type' />
                        </SelectTrigger>
                        <SelectContent>
                          {types.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className='space-y-1'>
                      <Label className='text-xs'>Initiation date</Label>
                      <DatePicker
                        datePickerSize='sm'
                        date={form.initiationDate}
                        maxDate={form.expirationDate}
                        onDateChange={date =>
                          setForm(prev => ({ ...prev, initiationDate: date }))
                        }
                        placeholder='mm/dd/yyyy'
                        showClearButton
                        aria-label='Initiation date'
                      />
                    </div>
                    <div className='space-y-1'>
                      <Label className='text-xs'>Expiration date</Label>
                      <DatePicker
                        datePickerSize='sm'
                        date={form.expirationDate}
                        minDate={form.initiationDate}
                        onDateChange={date =>
                          setForm(prev => ({ ...prev, expirationDate: date }))
                        }
                        placeholder='mm/dd/yyyy'
                        showClearButton
                        aria-label='Expiration date'
                      />
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <Label className='text-xs'>Notes</Label>
                    <Textarea
                      textareaSize='sm'
                      value={form.notes}
                      onChange={event =>
                        setForm(prev => ({
                          ...prev,
                          notes: event.target.value,
                        }))
                      }
                      placeholder='License number, issuing state, anything worth remembering'
                      aria-label='Notes'
                    />
                  </div>
                  <div className='flex flex-wrap justify-end gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={closeForm}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      onClick={submitForm}
                      disabled={isSubmitting}
                    >
                      {isSubmitting && (
                        <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                      )}
                      {editingId ? 'Save license' : 'Add license'}
                    </Button>
                  </div>
                </div>
              )}

              {(!inDialog || !canEdit) &&
                !isLoading &&
                licenses.length === 0 && (
                  <p className='text-sm text-muted-foreground'>
                    No licenses on file.
                  </p>
                )}

              {!inDialog && licenses.length > 0 && (
                // Scrolls inside its own container rather than collapsing into
                // very tall wrapped rows on a phone.
                <div className='overflow-x-auto rounded-md border'>
                  <Table className='min-w-[640px]'>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='h-8 py-1.5 align-middle text-xs'>
                          Type
                        </TableHead>
                        <TableHead className='h-8 py-1.5 align-middle text-xs'>
                          Initiation
                        </TableHead>
                        <TableHead className='h-8 py-1.5 align-middle text-xs'>
                          Expiration
                        </TableHead>
                        <TableHead className='h-8 py-1.5 align-middle text-xs'>
                          Notes
                        </TableHead>
                        <TableHead className='h-8 py-1.5 align-middle text-xs'>
                          Documents
                        </TableHead>
                        {canEdit && (
                          <TableHead className='h-8 py-1.5 text-right align-middle text-xs'>
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {licenses.map(license => (
                        <TableRow key={license.id}>
                          <TableCell className='py-1.5 align-top text-sm font-medium'>
                            {license.license_type_name}
                          </TableCell>
                          <TableCell className='py-1.5 align-top text-sm text-muted-foreground'>
                            {license.initiation_date
                              ? formatDateToMMDDYYYY(license.initiation_date)
                              : '—'}
                          </TableCell>
                          <TableCell className='py-1.5 align-top text-sm text-muted-foreground'>
                            <span className='whitespace-nowrap'>
                              {license.expiration_date
                                ? formatDateToMMDDYYYY(license.expiration_date)
                                : '—'}
                              <ExpiryBadge
                                expirationDate={license.expiration_date}
                                className='ml-2'
                              />
                            </span>
                          </TableCell>
                          <TableCell className='max-w-[14rem] py-1.5 align-top text-sm text-muted-foreground'>
                            <span className='break-words'>
                              {license.notes || '—'}
                            </span>
                          </TableCell>
                          <TableCell className='py-1.5 align-top'>
                            <div className='flex flex-col gap-1'>
                              {(license.documents ?? []).length === 0 && (
                                <span className='text-sm text-muted-foreground'>
                                  —
                                </span>
                              )}
                              {(license.documents ?? []).map(document => (
                                <div
                                  key={document.id}
                                  className='flex items-center gap-1'
                                >
                                  <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    onClick={() =>
                                      openDocument(license.id, document.id)
                                    }
                                  >
                                    <Download className='mr-1 h-3.5 w-3.5' />
                                    <span className='max-w-[10rem] truncate'>
                                      {document.file_name}
                                    </span>
                                  </Button>
                                  {canEdit && (
                                    <Button
                                      type='button'
                                      variant='ghost'
                                      size='sm'
                                      aria-label={`Remove ${document.file_name}`}
                                      disabled={busyLicenseId === license.id}
                                      onClick={() =>
                                        setPendingDelete({
                                          kind: 'document',
                                          licenseId: license.id,
                                          documentId: document.id,
                                          label: document.file_name,
                                        })
                                      }
                                    >
                                      <Trash2 className='h-3.5 w-3.5 text-destructive' />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              {canEdit && (
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  className='w-fit'
                                  disabled={busyLicenseId === license.id}
                                  onClick={() => beginUpload(license.id)}
                                >
                                  <Paperclip className='mr-1 h-3.5 w-3.5' />
                                  Attach
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          {canEdit && (
                            <TableCell className='py-1.5 text-right align-top'>
                              <div className='flex flex-wrap justify-end gap-1'>
                                <Button
                                  type='button'
                                  variant='outline'
                                  size='sm'
                                  onClick={() => openEditForm(license)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  type='button'
                                  variant='ghost'
                                  size='sm'
                                  aria-label={`Remove ${license.license_type_name}`}
                                  disabled={busyLicenseId === license.id}
                                  onClick={() =>
                                    setPendingDelete({
                                      kind: 'license',
                                      licenseId: license.id,
                                      label: license.license_type_name,
                                    })
                                  }
                                >
                                  {busyLicenseId === license.id ? (
                                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                                  ) : (
                                    <Trash2 className='h-3.5 w-3.5 text-destructive' />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {pendingDelete && (
                <ConfirmationDialog
                  open
                  title={
                    pendingDelete.kind === 'license'
                      ? 'Remove license'
                      : 'Remove document'
                  }
                  message={
                    pendingDelete.kind === 'license'
                      ? `This removes ${pendingDelete.label} and any documents attached to it. This cannot be undone.`
                      : `This permanently deletes ${pendingDelete.label}. This cannot be undone.`
                  }
                  confirmLabel='Remove'
                  // Both handlers close this dialog as they start, so the row
                  // spinner is what reports progress and this never renders
                  // true. Kept so the dialog's own in-flight guards are correct
                  // if the flow ever holds it open to show a failure inline.
                  loading={busyLicenseId === pendingDelete.licenseId}
                  onAccept={() => {
                    const target = resolveDeleteTarget(licenses, pendingDelete);
                    if (!target) {
                      setPendingDelete(null);
                      return;
                    }
                    if (target.kind === 'license') {
                      void removeLicense(target.license);
                      return;
                    }
                    void removeDocument(target.licenseId, target.documentId);
                  }}
                  onCancel={() => setPendingDelete(null)}
                />
              )}

              {canEdit && (
                // One hidden input serves every row: the row that opened it is
                // held in a ref, so the browser dialog is the only thing on
                // screen and no per-row input has to be rendered.
                <Input
                  ref={fileInputRef}
                  type='file'
                  className='hidden'
                  // Mirrors the server's allow-list so the picker does not
                  // offer a file the upload will refuse.
                  accept='.pdf,.png,.jpg,.jpeg,.heic,.heif,.webp,.tif,.tiff,application/pdf,image/png,image/jpeg,image/heic,image/heif,image/webp,image/tiff'
                  onChange={handleFileSelected}
                  aria-hidden='true'
                  tabIndex={-1}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  });
